import * as React from 'react';
import { useParams } from "react-router-dom";
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import { Delta } from '../../dashboard/components/Delta';
import { BotStatsInfo } from '../../../models/bot';
import { botStatsApi } from "../botsApi";
import { NotificationService } from "../../../common/notification";
import { FilterBar } from '../../dashboard/components';
import { toInputValue, subtractMonths } from '../../dashboard/utils/date';
import { TimeGranularity } from '../../../models/paymentSummary';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTheme } from '@mui/joy/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CombinedPoint } from '../../dashboard/components/CombinedProfitChart';

const calculatePercent = (part: number, total: number): number => {
    if (!total) {
        return 0;
    }
    return (part / total) * 100;
};

export function BotStats() {
    const {id} = useParams();
    const botId = Number(id);

    const [fromDate, setFromDate] = React.useState<string>(() => {
        const now = new Date();
        const from = subtractMonths(now, 1);
        return toInputValue(from);
    });
    const [toDate, setToDate] = React.useState<string>(() => toInputValue(new Date()));
    const [granularity, setGranularity] = React.useState<TimeGranularity>(TimeGranularity.Day);

    const [loading, setLoading] = React.useState(false);
    const [stats, setStats] = React.useState<BotStatsInfo>({
        statsDataPoints: [],
        totalSuccesses: 0,
        totalFailures: 0,
        totalSuccessesPrevious: 0,
        totalFailuresPrevious: 0
    });

    React.useEffect(() => {
        if (botId <= 0) {
            return;
        }

        loadData().catch(console.error);
    }, [botId, fromDate, toDate, granularity])

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formatBucketLabel = React.useCallback(
        (d: Date): string => {
            const locale =
                typeof navigator !== 'undefined' && navigator.language
                    ? navigator.language
                    : 'ru-RU';
            switch (granularity) {
                case TimeGranularity.Hour:
                    return new Intl.DateTimeFormat(locale, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                    }).format(d);
                case TimeGranularity.Month:
                    return new Intl.DateTimeFormat(locale, {
                        year: 'numeric',
                        month: '2-digit',
                    }).format(d);
                case TimeGranularity.Day:
                default:
                    return new Intl.DateTimeFormat(locale, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                    }).format(d);
            }
        },
        [granularity],
    );

    const dataset = React.useMemo(() => {
        if (stats.statsDataPoints.length === 0) {
            return [] as CombinedPoint[];
        }

        const from = new Date(fromDate);
        const to = new Date(toDate);

        const bucketKey = (d: Date): string => {
            switch (granularity) {
                case TimeGranularity.Hour:
                    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')} ${d.getHours().toString().padStart(2,'0')}:00`;
                case TimeGranularity.Day:
                    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
                case TimeGranularity.Month:
                    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}`;
                case TimeGranularity.Year:
                    return `${d.getFullYear()}`;
                default:
                    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}`;
            }
        };

        const keyToDate = (key: string): Date => {
            const parts = key.split(/[-\s:]/);
            const y = parseInt(parts[0], 10);
            const m = parts.length > 1 ? parseInt(parts[1], 10) - 1 : 0;
            const day = parts.length > 2 ? parseInt(parts[2], 10) : 1;
            const hour = parts.length > 3 ? parseInt(parts[3], 10) : 0;
            return new Date(y, m, day, hour, 0, 0, 0);
        };

        const sumByKey = (points: { bucketUtc: string; value: number, success: boolean }[], success: boolean) => {
            const map = new Map<string, number>();
            for (const p of points) {
                const t = new Date(p.bucketUtc);
                if (t < from || t > to || p.success !== success) {
                    continue;
                }
                const key = bucketKey(t);
                map.set(key, (map.get(key) ?? 0) + (p.value ?? 0));
            }
            return map;
        };

        const okMap = sumByKey(stats.statsDataPoints ?? [], true);
        const failMap = sumByKey(stats.statsDataPoints ?? [], false);

        // Build a continuous range of bucket keys from `from` to `to` for the selected granularity
        const addStep = (d: Date): Date => {
            switch (granularity) {
                case TimeGranularity.Hour: {
                    const nd = new Date(d);
                    nd.setHours(nd.getHours() + 1, 0, 0, 0);
                    return nd;
                }
                case TimeGranularity.Day: {
                    const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
                    return nd;
                }
                case TimeGranularity.Month: {
                    const nd = new Date(d.getFullYear(), d.getMonth() + 1, 1);
                    return nd;
                }
                case TimeGranularity.Year: {
                    const nd = new Date(d.getFullYear() + 1, 0, 1);
                    return nd;
                }
                default: {
                    const nd = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
                    return nd;
                }
            }
        };

        const startBucket = (() => {
            switch (granularity) {
                case TimeGranularity.Hour:
                    return new Date(from.getFullYear(), from.getMonth(), from.getDate(), from.getHours(), 0, 0, 0);
                case TimeGranularity.Day:
                    return new Date(from.getFullYear(), from.getMonth(), from.getDate());
                case TimeGranularity.Month:
                    return new Date(from.getFullYear(), from.getMonth(), 1);
                case TimeGranularity.Year:
                    return new Date(from.getFullYear(), 0, 1);
                default:
                    return new Date(from.getFullYear(), from.getMonth(), from.getDate());
            }
        })();

        const endTime = to.getTime();
        const allKeys: string[] = [];
        for (let d = startBucket; d.getTime() <= endTime; d = addStep(d)) {
            allKeys.push(bucketKey(d));
            // Safety to avoid infinite loops if something goes wrong
            if (allKeys.length > 100000) break;
        }

        return allKeys.map(k => ({
            x: keyToDate(k),
            successes: okMap.get(k) ?? 0,
            failures: failMap.get(k) ?? 0,
            total: (okMap.get(k) ?? 0) + (failMap.get(k) ?? 0),
        }));
    }, [stats]);

    const loadData = async () => {
        setLoading(true)
        try {
            let result = await botStatsApi(botId, {
                fromUtc: fromDate,
                toUtc: toDate,
                granularity: granularity
            });
            if (result.result) {
                const res = result.value!;
                setStats(res)
            } else {
                NotificationService.alertError("Не удалось загрузить статистику. Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false)
        }
    }

    const totalCount = React.useMemo(
        () => stats.totalSuccesses + stats.totalFailures,
        [stats],
    );
    const totalCountPrev = React.useMemo(
        () => stats.totalSuccessesPrevious + stats.totalFailuresPrevious,
        [stats],
    );
    const successPercent = React.useMemo(
        () => calculatePercent(stats.totalSuccesses, stats.totalSuccesses + stats.totalFailures),
        [stats],
    );
    const successPercentPrev = React.useMemo(
        () => calculatePercent(stats.totalSuccessesPrevious, stats.totalSuccessesPrevious + stats.totalFailuresPrevious),
        [stats],
    );
    const failPercent = React.useMemo(
        () => calculatePercent(stats.totalFailures, stats.totalSuccesses + stats.totalFailures),
        [stats],
    );
    const failPercentPrev = React.useMemo(
        () => calculatePercent(stats.totalFailuresPrevious, stats.totalSuccessesPrevious + stats.totalFailuresPrevious),
        [stats],
    );

    const successColor = successPercent >= successPercentPrev ? 'success.600' : 'danger.600';
    const failColor = failPercent <= failPercentPrev ? 'success.600' : 'danger.600';

    return (
        <React.Fragment>
            <FilterBar
                loading={loading}
                onApplyRange={(from, to) => { setFromDate(from); setToDate(to); }}
            />

            <Sheet
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyRoundedIcon fontSize="small" />
                    <Typography level="title-md">Статистика обменов</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography level="body-sm">Всего обменов</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: 1,
                                rowGap: 0.25,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Tooltip
                                title="Всего обменов за выбранный (прошлый) периоды"
                                disablePortal={false}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        columnGap: 1,
                                    }}
                                >
                                    <Typography
                                        level="title-lg"
                                        sx={{
                                            color:
                                                totalCount >= totalCountPrev
                                                    ? 'success.600'
                                                    : 'danger.600',
                                        }}
                                    >
                                        +{totalCount.toString()}
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                        (+{totalCountPrev})
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip
                                title="Прирост относительно прошлого периода"
                                disablePortal={false}
                            >
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta
                                        current={totalCount}
                                        previous={totalCountPrev}
                                        hideZeroPercent
                                    />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box>
                        <Typography level="body-sm">Успешные</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: 1,
                                rowGap: 0.25,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Tooltip
                                title="Успешные обмены за выбранный (прошлый) периоды"
                                disablePortal={false}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        columnGap: 1,
                                    }}
                                >
                                    <Typography
                                        level="title-lg"
                                        sx={{
                                            color:
                                                stats.totalSuccesses >= stats.totalSuccessesPrevious
                                                    ? 'success.600'
                                                    : 'danger.600',
                                        }}
                                    >
                                        +{stats.totalSuccesses}
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                        (+{stats.totalSuccessesPrevious})
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip
                                title="Прирост относительно прошлого периода"
                                disablePortal={false}
                            >
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta
                                        current={stats.totalSuccesses}
                                        previous={stats.totalSuccessesPrevious}
                                        hideZeroPercent
                                    />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box>
                        <Typography level="body-sm">Неуспешные</Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                columnGap: 1,
                                rowGap: 0.25,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Tooltip
                                title="Неуспешные обмены за выбранный (прошлый) периоды"
                                disablePortal={false}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        columnGap: 1,
                                    }}
                                >
                                    <Typography
                                        level="title-lg"
                                        sx={{
                                            color:
                                                stats.totalFailures <= stats.totalFailuresPrevious
                                                    ? 'success.600'
                                                    : 'danger.600',
                                        }}
                                    >
                                        +{stats.totalFailures}
                                    </Typography>
                                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                                        (+{stats.totalFailuresPrevious})
                                    </Typography>
                                </Box>
                            </Tooltip>
                            <Tooltip
                                title="Прирост относительно прошлого периода"
                                disablePortal={false}
                            >
                                <Box sx={{ ml: 0.5 }}>
                                    <Delta
                                        current={stats.totalFailures}
                                        previous={stats.totalFailuresPrevious}
                                        invertColor
                                        hideZeroPercent
                                    />
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>

                    <Box sx={{ flexBasis: '100%', width: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box>
                                <Typography level="body-sm">Процент успешных</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: 1,
                                        rowGap: 0.25,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Tooltip
                                        title="Процент успешных обменов за выбранный (прошлый) периоды"
                                        disablePortal={false}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                columnGap: 1,
                                            }}
                                        >
                                            <TrendingUpRoundedIcon
                                                fontSize="small"
                                                sx={{ color: successColor }}
                                            />
                                            <Typography
                                                level="title-lg"
                                                sx={{ color: successColor }}
                                            >
                                                {successPercent.toFixed(2)}%
                                            </Typography>
                                            <Typography
                                                level="body-sm"
                                                sx={{ color: 'neutral.500' }}
                                            >
                                                ({successPercentPrev.toFixed(2)}%)
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip
                                        title="Прирост относительно прошлого периода"
                                        disablePortal={false}
                                    >
                                        <Box sx={{ ml: 0.5 }}>
                                            <Delta
                                                current={successPercent}
                                                previous={successPercentPrev}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <Box>
                                <Typography level="body-sm">Процент неуспешных</Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        columnGap: 1,
                                        rowGap: 0.25,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Tooltip
                                        title="Процент неуспешных обменов за выбранный (прошлый) периоды"
                                        disablePortal={false}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                columnGap: 1,
                                            }}
                                        >
                                            <TrendingUpRoundedIcon
                                                fontSize="small"
                                                sx={{ color: failColor }}
                                            />
                                            <Typography
                                                level="title-lg"
                                                sx={{ color: failColor }}
                                            >
                                                {failPercent.toFixed(2)}%
                                            </Typography>
                                            <Typography
                                                level="body-sm"
                                                sx={{ color: 'neutral.500' }}
                                            >
                                                ({failPercentPrev.toFixed(2)}%)
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Изменение относительно прошлого периода (меньше — лучше)" disablePortal={false}>
                                        <Box sx={{ ml: 0.5 }}>
                                            <Delta current={failPercent}
                                                   previous={failPercentPrev}
                                                   invertColor
                                                   hideZeroPercent />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Sheet>

            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 1.5,
                        flexWrap: 'wrap',
                    }}
                >
                    <FormControl size="sm" sx={{ minWidth: 160 }}>
                        <FormLabel>Группировка</FormLabel>
                        <Select
                            value={granularity}
                            onChange={(_, value) =>
                                value != null && setGranularity(value as TimeGranularity)
                            }
                        >
                            <Option value={TimeGranularity.Hour}>По часам</Option>
                            <Option value={TimeGranularity.Day}>По дням</Option>
                            <Option value={TimeGranularity.Month}>По месяцам</Option>
                        </Select>
                    </FormControl>
                </Box>
                {stats.statsDataPoints.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography level="body-sm" color="neutral">
                            Нет данных для отображения
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 1, touchAction: 'pan-y' }}>
                        {isMobile ? (
                            <BarChart
                                dataset={dataset}
                                layout="horizontal"
                                margin={{ top: 8, right: 16, bottom: 96, left: 120 }}
                                yAxis={[
                                    {
                                        dataKey: 'x',
                                        scaleType: 'band',
                                        valueFormatter: (v: Date) => formatBucketLabel(v),
                                    },
                                ]}
                                xAxis={[
                                    {
                                        valueFormatter: (v: number) =>
                                            v.toLocaleString('ru-RU'),
                                    },
                                ]}
                                series={[
                                    {
                                        dataKey: 'total',
                                        label: 'Всего обменов',
                                        color: '#1976d2',
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                    {
                                        dataKey: 'successes',
                                        label: 'Успешные',
                                        color: '#2e7d32',
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                    {
                                        dataKey: 'failures',
                                        label: 'Неуспешные',
                                        color: '#d32f2f',
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                ]}
                                slotProps={{
                                    legend: {
                                        position: {
                                            vertical: 'bottom',
                                            horizontal: 'middle',
                                        },
                                    },
                                }}
                                height={dataset.length < 7 ? 180 : dataset.length * 40}
                            />
                        ) : (
                            <LineChart
                                dataset={dataset}
                                margin={{ top: 24, right: 24, bottom: 64, left: 24 }}
                                xAxis={[
                                    {
                                        dataKey: 'x',
                                        scaleType: 'time',
                                        valueFormatter: (v: Date) => formatBucketLabel(v),
                                    },
                                ]}
                                series={[
                                    {
                                        dataKey: 'total',
                                        label: 'Всего обменов',
                                        color: '#1976d2',
                                        connectNulls: true,
                                        curve: 'monotoneX',
                                        showMark: true,
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                    {
                                        dataKey: 'successes',
                                        label: 'Успешные',
                                        color: '#2e7d32',
                                        connectNulls: true,
                                        curve: 'monotoneX',
                                        showMark: true,
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                    {
                                        dataKey: 'failures',
                                        label: 'Неуспешные',
                                        color: '#d32f2f',
                                        connectNulls: true,
                                        curve: 'monotoneX',
                                        showMark: true,
                                        valueFormatter: (v: number | null) =>
                                            v != null ? v.toLocaleString('ru-RU') : '—',
                                    },
                                ]}
                                slotProps={{
                                    legend: {
                                        position: {
                                            vertical: 'bottom',
                                            horizontal: 'middle',
                                        },
                                    },
                                }}
                                height={400}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </React.Fragment>
    );
}